import React, { useEffect } from 'react';

import { Button } from '@components/index';

import FileAttachment from './FileAttachment';

import { TimelineItemProps } from '../../types/leadActivity';
import { formatTime } from '../../utils/common';
import {
  generateDescription,
  generateTitle,
  getEntityIcon
} from '../../utils/leadActivity';

const TimelineItem = React.memo<TimelineItemProps>(({ item, isLast }) => {
  const IconComponent = getEntityIcon(item.entity);
  const title = generateTitle(item);
  const description = generateDescription(item);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);
  const descriptionRef = React.useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (descriptionRef.current) {
      setShowMore(
        descriptionRef.current.scrollHeight >
          descriptionRef.current.clientHeight
      );
    }
  }, [description]);

  return (
    <div key={`${item.entity}-${item.timestamp}`} className="flex gap-3">
      <div className="mt-1 w-15 text-center">
        <span className="text-xs font-medium text-gray-500">
          {formatTime(item.timestamp)}
        </span>
      </div>
      <div className="flex flex-col items-center">
        <div className="flex size-9 max-h-[36px] max-w-[36px] shrink-0 items-center justify-center rounded-full bg-gray-100">
          <IconComponent className="size-5 text-gray-500" />
        </div>
        {!isLast && (
          <div className="h-full min-h-15 w-0.5 border-l border-dashed border-gray-300" />
        )}
      </div>

      <div className="flex flex-1 gap-3">
        <div className="flex-1 pb-4">
          <div className="mb-1">
            <div className="text-base font-medium text-content">{title}</div>
          </div>

          {description && (
            <div>
              <p
                ref={descriptionRef}
                className={`break-all text-sm  leading-5 text-gray-600 ${
                  !isExpanded ? 'line-clamp-4' : ''
                }`}
              >
                {description?.trim()}
              </p>
              {showMore && (
                <Button
                  handleClick={() => setIsExpanded(!isExpanded)}
                  className="!h-fit !p-0 text-xs text-gray-600"
                  variant="transparent"
                >
                  {isExpanded ? 'see less' : 'see more'}
                </Button>
              )}
            </div>
          )}

          <FileAttachment
            modifiedData={item.modified_data}
            actionType={item.action_type}
            entity={item.entity}
          />
        </div>
      </div>
    </div>
  );
});

export default TimelineItem;
